import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Modal } from '@/components/Modal';
import { statusLabels } from '@/sections/attacks/attackPaths/constants';
import type {
  CustomAssetNode,
  StageAction,
  TechniqueStatus,
} from '@/sections/attacks/attackPaths/types';

const STATUS_OPTIONS: TechniqueStatus[] = [
  'untested',
  'tested',
  'undetected',
  'logged',
  'alerted',
  'detected',
  'prevented',
];

interface AddActionModalProps {
  open: boolean;
  onClose: () => void;
  assetNodes: CustomAssetNode[];
  /** Pre-selected asset node ID (when triggered from graph) */
  defaultAssetNodeId?: string;
  onSubmit: (action: Omit<StageAction, 'id'>) => void;
}

export function AddActionModal({
  open,
  onClose,
  assetNodes,
  defaultAssetNodeId,
  onSubmit,
}: AddActionModalProps) {
  const [label, setLabel] = useState('');
  const [techniqueId, setTechniqueId] = useState('');
  const [status, setStatus] = useState<TechniqueStatus>('untested');
  const [details, setDetails] = useState('');
  const [assetNodeId, setAssetNodeId] = useState(defaultAssetNodeId || '');

  const resetForm = useCallback(() => {
    setLabel('');
    setTechniqueId('');
    setStatus('untested');
    setDetails('');
    setAssetNodeId(defaultAssetNodeId || '');
  }, [defaultAssetNodeId]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = useCallback(() => {
    if (!label.trim()) {
      toast.error('Action label is required');
      return;
    }
    onSubmit({
      label: label.trim(),
      techniqueId: techniqueId.trim() || undefined,
      status,
      details: details.trim() || undefined,
      assetNodeId: assetNodeId || undefined,
    });
    resetForm();
    onClose();
  }, [
    label,
    techniqueId,
    status,
    details,
    assetNodeId,
    onSubmit,
    onClose,
    resetForm,
  ]);

  return (
    <Modal
      title="Add On-Host Action"
      open={open}
      onClose={handleClose}
      className="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1">
          <label className="text-chariot-text-primary text-sm font-medium">
            Action <span className="text-chariot-status-critical">*</span>
          </label>
          <input
            type="text"
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="e.g., Dump keychain, Drop SSH key"
            className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary rounded-md border px-3 py-2 text-sm"
            autoFocus
          />
        </div>

        {assetNodes.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-chariot-text-primary text-sm font-medium">
              Target Host
            </label>
            <select
              value={assetNodeId}
              onChange={e => setAssetNodeId(e.target.value)}
              className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary rounded-md border px-3 py-2 text-sm"
            >
              <option value="">None (general)</option>
              {assetNodes.map(n => (
                <option key={n.id} value={n.id}>
                  {n.data.displayName || n.data.id}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex gap-3">
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-chariot-text-primary text-sm font-medium">
              MITRE ID
            </label>
            <input
              type="text"
              value={techniqueId}
              onChange={e => setTechniqueId(e.target.value)}
              placeholder="T1098"
              className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary rounded-md border px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-chariot-text-primary text-sm font-medium">
              Status
            </label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value as TechniqueStatus)}
              className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary rounded-md border px-3 py-2 text-sm"
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>
                  {statusLabels[s]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-chariot-text-primary text-sm font-medium">
            Details
          </label>
          <textarea
            value={details}
            onChange={e => setDetails(e.target.value)}
            placeholder="What happened and why it matters..."
            rows={2}
            className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary rounded-md border px-3 py-2 text-sm"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!label.trim()}
          >Add</Button>
        </div>
      </div>
    </Modal>
  );
}
